import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Logo } from "../brand/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYou() {
  const [params] = useSearchParams();
  const pi = params.get("pi");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(pi));

  useEffect(() => {
    if (!pi) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/${pi}`);
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus({ status: "unknown" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pi]);

  return (
    <main data-testid="thank-you-page" className="min-h-screen bg-bone text-ink flex flex-col">
      <div className="px-6 md:px-12 lg:px-16 py-8">
        <Logo variant="compact" />
      </div>
      <div className="flex-1 flex items-center">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24 w-full">
          <CheckCircle2 className="w-14 h-14 text-terracotta mb-8" strokeWidth={1.5} />
          <div className="overline text-terracotta mb-4">Order received</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight leading-[1.02]">
            Thank you.
            <br />
            <span className="font-wordmark italic font-semibold text-ink/85">
              You’re fuelled.
            </span>
          </h1>
          <p className="mt-6 text-ink/75 text-lg leading-relaxed max-w-xl">
            We’ve received your order and will send a confirmation email shortly.
            If you have questions, reply to that email — we read every one.
          </p>

          <div
            className="mt-10 bg-surface border border-hairline rounded-sm p-6 max-w-xl"
            data-testid="thank-you-summary"
          >
            <span className="overline text-inkmute block mb-2">Payment reference</span>
            <span className="font-display font-bold text-ink text-base break-all">
              {pi || "—"}
            </span>
            {loading && (
              <p className="mt-3 text-sm text-inkmute">Checking payment status…</p>
            )}
            {!loading && status && (
              <p className="mt-3 text-sm text-inkmute" data-testid="thank-you-status">
                Status:{" "}
                <span className="font-semibold text-ink uppercase">
                  {status.status || "unknown"}
                </span>
                {status.amount && status.currency_code
                  ? ` · ${(status.amount / 100).toFixed(2)} ${status.currency_code}`
                  : ""}
              </p>
            )}
          </div>

          <div className="mt-10">
            <Link
              to="/"
              data-testid="thank-you-home-link"
              className="inline-flex items-center gap-2 bg-ink text-bone hover:bg-terracotta transition-colors duration-300 px-6 py-3.5 rounded-sm font-display font-semibold text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Thrive by Madsen
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
